import { MoneyMapStatus } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useGetIncomeSplitOverviewFromGlobalState } from '../../banking/components/income-split/hooks/useGetIncomeSplitOverviewFromGlobalState';
import { invalidateIncomeSplitAndMoneyMapQueries } from '../../banking/hooks/income-split/invalidateIncomeSplitAndMoneyMapQueries';
import { useMembershipMoneyMaps } from './useMembershipMoneyMaps';
import { useMoneyMapWorkflow } from './useMoneyMapWorkflow';

export const useRefetchMoneySystemTab = () => {
    const queryClient = useQueryClient();

    return useCallback(async () => {
        await invalidateIncomeSplitAndMoneyMapQueries(queryClient);
    }, [queryClient]);
};

export const useMoneySystemTab = () => {
    const membershipMaps = useMembershipMoneyMaps();
    const { moneyMap, isWorkflowCompleted, isLoadingCompleteness } =
        useMoneyMapWorkflow();
    const { data: incomeSplitData } =
        useGetIncomeSplitOverviewFromGlobalState();
    const refetch = useRefetchMoneySystemTab();

    const {
        workflowMoneyMap,
        previewMoneyMap,
        liveSplitMoneyMap,
        currentIncomeSplit,
        areSame,
    } = membershipMaps;

    const showReviewUpdatedCard =
        !isLoadingCompleteness &&
        workflowMoneyMap?.status === MoneyMapStatus.ACTIVE &&
        !isWorkflowCompleted;

    // New PREVIEW while split still runs on a different map (ACTIVE or archived).
    const showExplorePreviewCard =
        previewMoneyMap != null &&
        currentIncomeSplit?.moneyMapId != null &&
        currentIncomeSplit.moneyMapId !== previewMoneyMap.id;

    const hasActiveMoneyMap = moneyMap?.status === MoneyMapStatus.ACTIVE;
    const hasIncomeSplitData =
        incomeSplitData != null && incomeSplitData.history.length > 0;

    return {
        ...membershipMaps,
        moneyMap,
        isWorkflowCompleted,
        isLoadingCompleteness,
        incomeSplitData,
        showReviewUpdatedCard,
        showExplorePreviewCard,
        showSeparateWorkflowCard:
            workflowMoneyMap != null && liveSplitMoneyMap != null && !areSame,
        showIncomeSplit:
            hasActiveMoneyMap ||
            hasIncomeSplitData ||
            liveSplitMoneyMap != null,
        refetch,
    };
};
